import React from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from 'component/Layout/Tooltip';
import { TooltipPosition } from 'component/Layout/Tooltip/types';

import classNames from 'classnames';

import { QaIdentifier, useQa } from 'hooks/useQa';

import { ReactComponent as IconLoyaltyBadge } from 'media/svg/loyaltyBadge.svg';
import { ReactComponent as IconThousandsSeparator } from 'media/svg/thousandsSeparator.svg';

import { getDecimalNumber } from 'utility/Format/Number';
import { getFormattedPrice, PriceFormatter } from 'utility/Format/Price';

import Icon, { LoyaltyValue } from './__partials/Icon';

import styles from './Loyalty.module.scss';

export interface LoyaltyProps {
    points: number;
    customClass?: string;
    isPointAccount?: boolean;
    tooltipConfig?: {
        placement?: TooltipPosition;
        align?: {
            offset: number[];
        };
    };
}

const Loyalty = ({
    customClass,
    isPointAccount = false,
    tooltipConfig,
    points,
}: LoyaltyProps) => {
    const { t } = useTranslation();
    const qaAttr = useQa(QaIdentifier.Incentive_Loyalty);

    return (
        <Tooltip
            content={
                <span
                    dangerouslySetInnerHTML={{
                        __html: isPointAccount
                            ? t('TRANS-loyalty-member-tooltip', {
                                  points: getDecimalNumber(points, 0),
                              })
                            : t('TRANS-loyalty-no-member-tooltip', {
                                  points: getDecimalNumber(points, 0),
                                  cash: getFormattedPrice(
                                      points * 0.01,
                                      PriceFormatter.WithFractionDigits
                                  ),
                              }),
                    }}
                />
            }
            placement={tooltipConfig?.placement ?? TooltipPosition.Right}
            align={tooltipConfig?.align ?? { offset: [15] }}
        >
            <div
                className={classNames(styles.Loyalty, customClass)}
                data-count={points}
                {...qaAttr}
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <IconLoyaltyBadge />
                <span
                    className={classNames({
                        [styles.Wrapper]: points < 10000,
                        [styles.WrapperOverTenThousand]: points > 9999,
                    })}
                >
                    {Array.from(String(points)).map((value, index) => {
                        if (points > 999 && points < 10000 && index === 1) {
                            return (
                                <span key={index}>
                                    <span className={styles.ThousandsSeparator}>
                                        <IconThousandsSeparator />
                                    </span>
                                    <span>
                                        <Icon value={value as LoyaltyValue} />
                                    </span>
                                </span>
                            );
                        }
                        if (points > 9999 && index === 2) {
                            return (
                                <span key={index}>
                                    <span className={styles.ThousandsSeparator}>
                                        <IconThousandsSeparator />
                                    </span>
                                    <span>
                                        <Icon value={value as LoyaltyValue} />
                                    </span>
                                </span>
                            );
                        }

                        return (
                            <span key={index}>
                                <Icon value={value as LoyaltyValue} />
                            </span>
                        );
                    })}
                    <br />
                    {t('TRANS-points[i]', {
                        postProcess: 'interval',
                        amount: 2,
                    })}
                </span>
            </div>
        </Tooltip>
    );
};

export default Loyalty;
